/**
 * Studios catalog + PERMISSION TREE seed (prototype screens 11/12/13/16).
 *
 * Each studio has top-level permission groups (Drives, Sandboxes …) whose
 * children may nest once more (View drives → Browse drives …). A row starts at
 * the narrowest level it accepts (see `utils/permissionTree.js`); the prototype's
 * `default_level` is not part of the catalog.
 */

export const PERMISSION_LEVELS = ['own', 'groups', 'tenant'];

const p = (id, label, description, children) => ({
  id,
  label,
  description,
  ...(children ? { children } : {}),
});

const buildGovernance = () => [
  p('governance.drives', 'Drives', 'Manage files and folders within governance drives', [
    p(
      'governance.drives.view',
      'View drives',
      'Browse and read files within authorized drives',
      [
        p('governance.drives.view.browse', 'Browse drives', 'List drives and folders'),
        p('governance.drives.view.read', 'Read files', 'Open and preview files'),
        p(
          'governance.drives.view.download',
          'Download files',
          'Download files to a device'
        ),
        p(
          'governance.drives.view.search',
          'Search files',
          'Search file names and content'
        ),
      ]
    ),
    p(
      'governance.drives.upload',
      'Upload files',
      'Add new files and documents to governance drives'
    ),
    p(
      'governance.drives.create_folders',
      'Create folders',
      'Organize content by creating folder structures'
    ),
  ]),
  p(
    'governance.sandboxes',
    'Sandboxes',
    'Work within isolated environments before promoting',
    [
      p(
        'governance.sandboxes.view',
        'View sandboxes',
        'List and browse available sandbox environments'
      ),
      p(
        'governance.sandboxes.details',
        'Sandbox details',
        'Access configuration and metadata for a sandbox'
      ),
      p(
        'governance.sandboxes.view_claims',
        'View claims',
        'See the knowledge claims stored in a sandbox'
      ),
      p(
        'governance.sandboxes.manage_bundles',
        'Manage bundles',
        'Create and edit content bundles within a sandbox'
      ),
      p(
        'governance.sandboxes.connect_sources',
        'Connect sources',
        'Link external data sources to a sandbox'
      ),
      p('governance.sandboxes.promote', 'Promote bundles', 'Submit a bundle for promotion'),
      p(
        'governance.sandboxes.delete',
        'Delete sandboxes',
        'Remove a sandbox and its content'
      ),
    ]
  ),
  p('governance.truth_plane', 'Truth Plane', 'Access the authoritative knowledge base', [
    p('governance.truth_plane.view', 'View facts', 'Browse approved facts and entities'),
    p('governance.truth_plane.edit', 'Edit facts', 'Propose changes to facts and entities'),
    p(
      'governance.truth_plane.approve',
      'Approve facts',
      'Approve proposed changes to facts'
    ),
  ]),
  p(
    'governance.promotion_packs',
    'Promotion Packs',
    'Bundle content changes for review and approval',
    [
      p(
        'governance.promotion_packs.view',
        'View packs',
        'Browse promotion packs and their status'
      ),
      p('governance.promotion_packs.create', 'Create packs', 'Assemble a pack of changes'),
      p(
        'governance.promotion_packs.approve',
        'Approve promotions',
        'Approve or reject a promotion pack'
      ),
    ]
  ),
];

const buildData = () => [
  p('data.datasets', 'Datasets', 'Manage datasets and their schemas', [
    p('data.datasets.view', 'View datasets', 'Browse datasets and sample rows'),
    p('data.datasets.create', 'Create datasets', 'Register or upload a new dataset'),
    p('data.datasets.schemas', 'Manage schemas', 'Edit dataset schemas and types'),
  ]),
  p('data.models', 'Models', 'Author and publish data models', [
    p('data.models.view', 'View models', 'Browse model definitions'),
    p('data.models.author', 'Author models', 'Create and edit model definitions'),
    p('data.models.publish', 'Publish models', 'Publish a model for use by others'),
  ]),
  p('data.lineage', 'Lineage', 'Explore and curate data lineage graphs', [
    p(
      'data.lineage.view',
      'View lineage graphs',
      'Explore upstream and downstream lineage'
    ),
    p('data.lineage.edit', 'Edit lineage', 'Add or correct lineage edges'),
  ]),
];

const buildAgentic = () => [
  p('agentic.workers', 'AI Workers', 'Configure and operate AI workers', [
    p(
      'agentic.workers.view',
      'View workers',
      'Browse AI workers and their configuration'
    ),
    p('agentic.workers.create', 'Create workers', 'Create and configure AI workers'),
    p('agentic.workers.deploy', 'Deploy workers', 'Deploy a worker to production'),
  ]),
  p('agentic.networks', 'Agentic Networks', 'Build and run agentic networks', [
    p('agentic.networks.view', 'View networks', 'Browse agentic networks'),
    p('agentic.networks.build', 'Build networks', 'Design and edit network workflows'),
    p('agentic.networks.run', 'Run networks', 'Start and stop network executions'),
  ]),
  p('agentic.htl_packs', 'HTL Packs', 'Human-in-the-loop handoff packs', [
    p('agentic.htl_packs.view', 'View packs', 'Browse HTL packs'),
    p('agentic.htl_packs.manage', 'Manage packs', 'Create and edit HTL packs'),
  ]),
];

export const buildSeedStudios = () => [
  {
    id: 'governance',
    name: 'Governance Studio',
    tag: 'Governance',
    icon: 'shield',
    description: 'Policy management, data lineage, and compliance workflows.',
    permissions: buildGovernance(),
  },
  {
    id: 'data',
    name: 'Data Studio',
    tag: 'Data',
    icon: 'database',
    description: 'Model authoring, dataset management, and schema design.',
    permissions: buildData(),
  },
  {
    id: 'agentic',
    name: 'Agentic Studio',
    tag: 'Agentic',
    icon: 'bot',
    description: 'AI worker configuration and agentic network management.',
    permissions: buildAgentic(),
  },
];

/** Depth-first list of every node of a permission tree, with its `parent_id`. */
export const flattenPermissions = (nodes, parent_id = null) =>
  nodes.flatMap((node) => [
    { ...node, children: undefined, parent_id },
    ...flattenPermissions(node.children ?? [], node.id),
  ]);
